import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, X } from "lucide-react";

import { Button } from "@/design-system/primitives";
import { useRecordClinicianVisitOverride } from "@/lib/api/clinicianHooks";
import type {
  BriefAppointment,
  PatientBriefResponse,
  PrescriptionWorkItem,
} from "@/types/clinician";

const OVERRIDE_REASONS = [
  { value: "visit_occurred", label: "Visit occurred" },
  { value: "external_visit", label: "Visit occurred outside Healthie" },
  {
    value: "tracking_issue",
    label: "Healthie or Zoom did not record the visit",
  },
  { value: "other", label: "Other documented visit" },
] as const;

type VisitOverrideReason = (typeof OVERRIDE_REASONS)[number]["value"];

export function preferredVisitTimestamp(
  appointments: BriefAppointment[],
  requiredAfter: string | null,
  now = Date.now(),
) {
  const requiredTime = requiredAfter ? Date.parse(requiredAfter) : Number.NaN;
  const eligible = appointments
    .map((appointment) => ({
      appointment,
      startsAt: Date.parse(appointment.starts_at),
    }))
    .filter(
      ({ appointment, startsAt }) =>
        Number.isFinite(startsAt) &&
        (!Number.isFinite(requiredTime) || startsAt >= requiredTime) &&
        !["cancelled", "no_show", "no show"].includes(
          String(appointment.status ?? "").toLowerCase(),
        ),
    );
  const mostRecentPast = eligible
    .filter(({ startsAt }) => startsAt <= now)
    .sort((left, right) => right.startsAt - left.startsAt)[0];
  if (mostRecentPast) return mostRecentPast.appointment.starts_at;
  const nextUpcoming = eligible.sort(
    (left, right) => left.startsAt - right.startsAt,
  )[0];
  return nextUpcoming?.appointment.starts_at ?? new Date(now).toISOString();
}

function localDateTimeInput(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localized = new Date(
    date.getTime() - date.getTimezoneOffset() * 60_000,
  );
  return localized.toISOString().slice(0, 16);
}

export function VisitOverrideModal({
  item,
  data,
  onClose,
  onComplete,
}: {
  item: PrescriptionWorkItem;
  data: PatientBriefResponse;
  onClose: () => void;
  onComplete: () => void;
}) {
  const recordOverride = useRecordClinicianVisitOverride(item.patient.id);
  const preferredVisit = useMemo(
    () =>
      preferredVisitTimestamp(data.appointments, item.requires_consult_after),
    [data.appointments, item.requires_consult_after],
  );
  const [reason, setReason] = useState<VisitOverrideReason>("visit_occurred");
  const [occurredAt, setOccurredAt] = useState(() =>
    localDateTimeInput(preferredVisit),
  );
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !recordOverride.isPending) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, recordOverride.isPending]);

  const submit = async () => {
    const occurred = new Date(occurredAt);
    if (!occurredAt || Number.isNaN(occurred.getTime())) {
      setError("Enter the date and time when the visit occurred.");
      return;
    }
    if (occurred.getTime() > Date.now()) {
      setError("A visit cannot be documented as completed before it occurs.");
      return;
    }
    setError(null);
    try {
      await recordOverride.mutateAsync({
        order_id: item.id,
        reason_code: reason,
        occurred_at: occurred.toISOString(),
        explanation: explanation.trim() || undefined,
        confirmed: true,
      });
      onComplete();
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "The visit override could not be recorded. Nothing was changed.",
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-ink/55 backdrop-blur-sm p-10 md:p-20 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-override-title"
    >
      <section className="w-full max-w-[56rem] bg-page border border-line shadow-xl">
        <header className="px-15 py-13 border-b border-line-subtle flex items-start justify-between gap-10">
          <div>
            <p className="type-label-xs text-warning">CLINICIAN ATTESTATION</p>
            <h2 id="visit-override-title" className="type-heading-lg mt-4">
              Document completed visit
            </h2>
            <p className="type-body-sm text-ink-muted mt-4">
              {item.patient.display_name || item.patient.email} · {item.product}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close visit override"
            disabled={recordOverride.isPending}
            onClick={onClose}
            className="p-5 text-ink-muted hover:text-ink disabled:opacity-40"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="p-15 flex flex-col gap-12">
          <div className="border border-warning/30 bg-warning-wash p-10 flex gap-8">
            <AlertTriangle size={16} className="text-warning shrink-0 mt-1" />
            <p className="type-body-sm text-ink-secondary">
              Record this only when the required live clinician visit actually
              occurred. Your identity, reason, and visit time are retained in
              the prescription audit history.
            </p>
          </div>

          <label className="flex flex-col gap-5">
            <span className="type-label-xs text-ink-muted">
              OVERRIDE REASON
            </span>
            <select
              value={reason}
              onChange={(event) =>
                setReason(event.target.value as VisitOverrideReason)
              }
              className="min-h-40 border border-line bg-raised px-10 type-body-sm"
            >
              {OVERRIDE_REASONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-5">
            <span className="type-label-xs text-ink-muted flex items-center gap-5">
              <CalendarClock size={13} aria-hidden /> VISIT DATE & TIME
            </span>
            <input
              type="datetime-local"
              value={occurredAt}
              onChange={(event) => setOccurredAt(event.target.value)}
              className="min-h-40 border border-line bg-raised px-10 type-body-sm"
            />
            <span className="text-11 text-ink-muted">
              Shown in your local timezone. Defaults to the relevant scheduled
              appointment when available.
            </span>
          </label>

          <label className="flex flex-col gap-5">
            <span className="type-label-xs text-ink-muted">
              EXPLANATION · OPTIONAL
            </span>
            <textarea
              value={explanation}
              onChange={(event) => setExplanation(event.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Add context about the completed visit or missing attendance record…"
              className="border border-line bg-raised px-10 py-8 type-body-sm outline-none focus:shadow-focus resize-y"
            />
          </label>

          {error && (
            <div className="border border-danger/30 bg-danger-wash p-9 type-body-sm text-danger">
              {error}
            </div>
          )}
        </div>

        <footer className="px-15 py-11 border-t border-line-subtle flex flex-wrap items-center justify-end gap-8">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={recordOverride.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            loading={recordOverride.isPending}
            disabled={!occurredAt}
            onClick={() => void submit()}
          >
            Confirm visit & prescribe
          </Button>
        </footer>
      </section>
    </div>
  );
}
